/**
 * ReopenRecordButton.jsx
 * CCH Care — Late Documentation Phase 2
 * Last Modified: 2026-05-13
 *
 * Care Supervisor / Care Admin control on the ADL summary page that
 * reopens a record for late documentation after the 2-hour grace
 * window has closed but within 24 hours of shift end.
 *
 * Per Policy v1.0 §4.4. Backend endpoint: POST /api/adl/reopen.php.
 *
 * Three render paths based on role + record state:
 *
 *   1. CNA viewer → renders null (no capability)
 *
 *   2. Supervisor/Admin + record already in active reopen window
 *      → renders an info pill showing the expiry, no button.
 *      Active = status='late_documentation' AND late_doc_expires_at > now.
 *
 *   3. Supervisor/Admin + reopen-eligible
 *      → renders the button. Eligibility is loosely checked here
 *      (record not in active reopen window). Strict eligibility
 *      (shift ended 2-24h ago) is enforced by the backend on POST;
 *      the user sees the friendly error message if they click on
 *      a record outside the window.
 *
 * Props:
 *   record        — the ADL record object from /shifts/history.php
 *   onReopened    — callback fired after a successful reopen so the
 *                   parent can refresh and re-render the new state
 */
import { useState } from 'react';
import { useAuth } from '../../features/auth/useAuth';
import client from '../../api/client';
import { COLORS } from '../../config/constants';

const REOPEN_ROLES = ['Care Supervisor', 'Care Admin'];

/**
 * Format an ISO/MySQL timestamp into a user-friendly absolute
 * time like "Wed, May 14 at 11:23 PM". Uses browser locale.
 */
function formatExpiresAt(timestamp) {
  if (!timestamp) return '';
  try {
    const dt = new Date(timestamp.replace(' ', 'T'));
    if (isNaN(dt.getTime())) return timestamp;
    const datePart = dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    const timePart = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${datePart} at ${timePart}`;
  } catch {
    return timestamp;
  }
}

export default function ReopenRecordButton({ record, onReopened }) {
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';

  const [showModal, setShowModal]   = useState(false);
  const [reason, setReason]         = useState('');
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState('');

  // ── Role gate ──────────────────────────────────────────────
  if (!REOPEN_ROLES.includes(role)) return null;
  if (!record) return null;

  // ── Already reopened? ──────────────────────────────────────
  // Check the record state to see if we're in an active reopen
  // window. If so, render the info pill instead of the button.
  const expiresAtMs = record.late_doc_expires_at
    ? new Date(record.late_doc_expires_at.replace(' ', 'T')).getTime()
    : 0;
  const isReopened = record.status === 'late_documentation' && expiresAtMs > Date.now();

  if (isReopened) {
    return <ReopenedBadge record={record} />;
  }

  // ── Modal handlers ─────────────────────────────────────────
  function openModal() {
    setReason('');
    setError('');
    setShowModal(true);
  }

  function closeModal() {
    if (busy) return;
    setShowModal(false);
    setReason('');
    setError('');
  }

  async function handleConfirm() {
    setBusy(true);
    setError('');
    try {
      const res = await client.post('/adl/reopen.php', {
        adl_record_id: record.adl_record_id,
        reason: reason.trim() || null,
      }, {
        // Don't fire the global access-denied handler on 403 — we
        // want to show the role-mismatch error inline in the modal
        // rather than redirect the user away from this page.
        meta: { silentOn403: true },
      });

      if (res.data?.success) {
        setShowModal(false);
        setReason('');
        if (typeof onReopened === 'function') onReopened(res.data);
      } else {
        setError(res.data?.error || 'Reopen failed. Try again.');
      }
    } catch (e) {
      // Backend 409s with friendly messages — surface them directly.
      setError(
        e?.response?.data?.error ||
        e?.message ||
        'Could not reopen this record. Try again.'
      );
    } finally {
      setBusy(false);
    }
  }

  // ── Render: button + modal ─────────────────────────────────
  return (
    <>
      <div style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.warning}`,
        borderLeft: `4px solid ${COLORS.warning}`,
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
        <span aria-hidden="true" style={{ fontSize: 20 }}>🔓</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>
            Reopen for late documentation
          </div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
            Allows the original CNA to complete entries for up to 24 hours.
            Audited as a supervisor override.
          </div>
        </div>
        <button
          onClick={openModal}
          style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: 'none',
            background: COLORS.warning,
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          Reopen
        </button>
      </div>

      {showModal && (
        <Overlay>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔓</div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, margin: '0 0 6px' }}>
            Reopen record for late documentation?
          </h2>
          <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '0 0 16px', lineHeight: 1.5 }}>
            The original CNA will be able to complete any missing sections for
            up to 24 hours from now. This action is recorded in the audit log
            with your name and any reason you provide.
          </p>

          <label style={{
            display: 'block', fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
            textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'left', marginBottom: 4,
          }} htmlFor="reopen-reason">
            Reason (optional)
          </label>
          <textarea
            id="reopen-reason"
            value={reason}
            onChange={e => { setReason(e.target.value); setError(''); }}
            placeholder="e.g., CNA confirmed lunch was documented verbally but not entered."
            rows={3}
            disabled={busy}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: `1.5px solid ${error ? COLORS.danger : COLORS.border}`,
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              outline: 'none',
              resize: 'vertical',
              background: COLORS.surface,
              color: COLORS.text,
              marginBottom: error ? 4 : 16,
            }}
          />

          {error && (
            <p style={{
              fontSize: 12, color: COLORS.danger, textAlign: 'left',
              margin: '0 0 12px',
            }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <ModalButton onClick={closeModal} variant="secondary" disabled={busy}>
              Cancel
            </ModalButton>
            <ModalButton onClick={handleConfirm} variant="primary" flex={2} disabled={busy}>
              {busy ? 'Reopening…' : 'Confirm Reopen'}
            </ModalButton>
          </div>
        </Overlay>
      )}
    </>
  );
}

// ── Reopened badge (shown when record is in active reopen window) ──
function ReopenedBadge({ record }) {
  return (
    <div style={{
      background: COLORS.warningLight,
      border: `1px solid ${COLORS.warning}`,
      borderLeft: `4px solid ${COLORS.warning}`,
      borderRadius: 10,
      padding: '12px 14px',
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <span aria-hidden="true" style={{ fontSize: 20 }}>🟡</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.warning }}>
          Record reopened for late documentation
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
          Window closes <strong>{formatExpiresAt(record.late_doc_expires_at)}</strong>.
          Original CNA can complete entries until then.
        </div>
      </div>
    </div>
  );
}

// ── Shared modal sub-components ────────────────────────────────────

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }}>
      <div style={{
        background: COLORS.surface, borderRadius: 20, padding: 28,
        maxWidth: 480, width: '100%', textAlign: 'center',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
      }}>
        {children}
      </div>
    </div>
  );
}

function ModalButton({ onClick, variant, children, flex = 1, disabled = false }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex,
        padding: '12px 16px', borderRadius: 10,
        border: isPrimary ? 'none' : `2px solid ${COLORS.border}`,
        background: isPrimary ? COLORS.warning : COLORS.surface,
        color: isPrimary ? '#fff' : COLORS.text,
        fontWeight: 700, fontSize: 14, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'opacity 0.15s',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}